/**
 * Fonts for plate words: the plates' own, every free Telugu font (each
 * weight), and fonts the channel uploads (any it has a licence for — Anu,
 * Windows' Gautami or Nirmala UI… — as long as it is Unicode Telugu).
 * Searched, filtered by kind, grouped by family (its weights as chips), each
 * shown written in itself.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch, FiUpload, FiLoader, FiTrash2, FiX, FiChevronDown, FiAlertTriangle } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { useAuthStore } from '../../utils/store';
import { showError } from '../FullStory/shared';

const MANAGERS = ['admin', 'superadmin', 'manager'];
const KINDS = ['all', 'builtin', 'uploaded', 'display', 'sans', 'serif', 'handwriting'];

// One list for the whole page; reloaded after a font is added or removed.
let fontsPromise = null;
const listeners = new Set();
export const loadFonts = () => {
  if (!fontsPromise) fontsPromise = api.plateFonts().catch((e) => { fontsPromise = null; throw e; });
  return fontsPromise;
};
export const reloadFonts = async () => {
  fontsPromise = null;
  const fonts = await loadFonts();
  listeners.forEach((l) => l(fonts));
  return fonts;
};
/** The fonts (null while loading), kept up to date. */
export const useFonts = () => {
  const [fonts, setFonts] = useState(null);
  useEffect(() => {
    let alive = true;
    loadFonts().then((f) => alive && setFonts(f)).catch(showError);
    const on = (f) => alive && setFonts(f);
    listeners.add(on);
    return () => { alive = false; listeners.delete(on); };
  }, []);
  return fonts;
};

/** Families, in list order: [{ group, fonts: [...] }]. */
const familiesOf = (fonts) => {
  const map = new Map();
  fonts.forEach((f) => { if (!map.has(f.group)) map.set(f.group, []); map.get(f.group).push(f); });
  return [...map.entries()].map(([group, list]) => ({ group, fonts: list }));
};
/** A font's weight label within its family ("Bold"), or its name. */
const styleOf = (f, group) => (f.name.startsWith(`${group} `) ? f.name.slice(group.length + 1) : f.name === group ? 'Regular' : f.name);

/** Add a font: a file, its name; refusals said here. */
const FontUpload = ({ onAdded }) => {
  const t = useT();
  const input = useRef(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const record = await api.uploadPlateFont(file, name.trim());
      await reloadFonts();
      setName('');
      onAdded?.(record);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'error');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div className="rounded-lg border border-dashed border-gray-300 p-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t('plateFonts.namePlaceholder')} className="input text-xs py-1 flex-1 min-w-[10rem]" />
        <input ref={input} type="file" accept=".ttf,.otf,.ttc" className="hidden" onChange={(e) => send(e.target.files?.[0])} />
        <button type="button" onClick={() => input.current?.click()} disabled={busy} className="btn btn-outline btn-sm text-xs flex items-center gap-1">
          {busy ? <FiLoader size={12} className="animate-spin" /> : <FiUpload size={12} />} {t('plateFonts.upload')}
        </button>
      </div>
      <p className="text-[10px] text-gray-500">{t('plateFonts.uploadHint')}</p>
      {error && <p className="text-[11px] text-red-700 flex items-start gap-1"><FiAlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {error}</p>}
    </div>
  );
};

/** Every font, to pick one: search, kind, families with their weights. */
export const FontLibrary = ({ value, onChange, maxHeight = '22rem' }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const { user } = useAuthStore();
  const fonts = useFonts();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const families = useMemo(() => {
    if (!fonts) return [];
    const q = query.trim().toLowerCase();
    return familiesOf(fonts.filter((f) => (kind === 'all' || f.category === kind)
      && (!q || f.name.toLowerCase().includes(q) || String(f.te || '').includes(query.trim()))));
  }, [fonts, query, kind]);
  const mine = (f) => f.uploaded && (f.createdBy === String(user?._id || user?.id) || MANAGERS.includes(user?.role));
  const remove = async (f) => {
    if (!window.confirm(t('plateFonts.removeConfirm', { name: f.group }))) return;
    try { await api.removePlateFont(f.uploadId); await reloadFonts(); } catch (e) { showError(e); }
  };
  if (!fonts) return <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.loading')}</p>;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[10rem]">
          <FiSearch size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('plateFonts.search', { count: fonts.length })} aria-label={t('plateFonts.search', { count: fonts.length })} className="input text-xs py-1" style={{ paddingLeft: '1.6rem' }} />
        </label>
      </div>
      <div className="flex flex-wrap gap-1">
        {KINDS.filter((k) => k === 'all' || fonts.some((f) => f.category === k)).map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} className={`px-2 py-0.5 rounded-full text-[11px] border ${kind === k ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
            {t(`plateFonts.kind.${k}`)}
          </button>
        ))}
      </div>
      <div className="overflow-y-auto pr-1 space-y-1" style={{ maxHeight }} role="radiogroup" aria-label={t('plateTemplates.font')}>
        {!families.length && <p className="text-xs text-gray-500 py-2">{t('plateFonts.none')}</p>}
        {families.map(({ group, fonts: list }) => {
          const chosen = list.find((f) => f.id === value);
          const shown = chosen || list.find((f) => /Regular$/.test(f.name) || f.name === group) || list[0];
          return (
            <div key={group} className={`rounded-lg border px-2 py-1.5 ${chosen ? 'border-primary-500 bg-primary-50/60' : 'border-gray-200 bg-white'}`}>
              <button type="button" role="radio" aria-checked={Boolean(chosen)} onClick={() => onChange(shown.id)} className="w-full text-left">
                {/* Sample is dark ink on transparent (drawn by the server): white in dark mode. */}
                <img src={api.plateFontSampleUrl(shown.id)} alt="" className="h-6 max-w-full object-contain object-left dark:invert" loading="lazy" />
              </button>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                <span className="text-[11px] font-medium text-gray-700 mr-1">{lang === 'te' && list.length === 1 ? list[0].te : group}</span>
                {list.length > 1 && list.map((f) => (
                  <button key={f.id} type="button" onClick={() => onChange(f.id)} aria-pressed={f.id === value} className={`px-1.5 py-0 rounded border text-[10px] ${f.id === value ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {styleOf(f, group)}
                  </button>
                ))}
                {list[0].uploaded && <span className="text-[10px] text-sky-700 bg-sky-50 rounded px-1">{t('plateFonts.uploadedBy', { name: list[0].createdByName || '—' })}</span>}
                {mine(list[0]) && <button type="button" onClick={() => remove(list[0])} title={t('plateTemplates.remove')} aria-label={t('plateTemplates.remove')} className="ml-auto text-gray-400 hover:text-red-600"><FiTrash2 size={12} /></button>}
              </div>
            </div>
          );
        })}
      </div>
      <FontUpload onAdded={(record) => { if (record?.id) onChange(record.weights?.length > 1 ? `${record.id}-${record.weights.includes(400) ? 400 : record.weights[0]}` : record.id); }} />
    </div>
  );
};

/** A compact font choice (a text area's): the font written in itself; opens the library. */
export const FontSelect = ({ value, onChange, label }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const fonts = useFonts();
  const [open, setOpen] = useState(false);
  const current = fonts?.find((f) => f.id === value);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={label} title={label} className="input text-xs py-1 mt-0.5 flex items-center gap-1 text-left w-full">
        <span className="flex-1 truncate">{current ? (lang === 'te' ? current.te : current.name) : value}</span>
        <FiChevronDown size={12} className="text-gray-400 flex-shrink-0" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-3" onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-label={t('plateTemplates.font')}>
          <div className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain bg-white rounded-2xl shadow-2xl p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.font')}</h3>
              <button type="button" onClick={() => setOpen(false)} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm px-1.5"><FiX size={16} /></button>
            </div>
            <FontLibrary value={value} onChange={(id) => { onChange(id); setOpen(false); }} maxHeight="55vh" />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
